import { get, isEmpty } from "lodash";
import React, { useCallback, useContext, useMemo } from "react";
import useSWR from "swr";
import { CurrencyKey } from "../../utils/useCurrency";

type ContextValue = {
  stats: Stats | undefined;
  isLoading: boolean;
  isError: boolean;
  getPercentile: (metric: string, value: number) => number | undefined;
};

const StatsContext = React.createContext<ContextValue | undefined>(undefined);

export const StatsProvider: React.FC<{}> = (props) => {
  const fetchStats = useCallback(async (): Promise<Stats> => {
    const response = await fetch(`${process.env.REACT_APP_ETL_API}/stats/`);

    const apiResponse: StatsResponse = await response.json();

    return {
      blocks: apiResponse.total_blocks,
      hotspots: apiResponse.total_hotspots,
      consensusGroups: apiResponse.total_consensus_groups,
      cities: apiResponse.total_cities,
      countries: apiResponse.total_countries,
      averageTransmitScale: apiResponse.average_reward_scale,
      dailyAverageHotspotRewards: apiResponse.daily_average_hotspot_rewards,
      averageHotspotTxRewardUnits: apiResponse.average_tx_reward_unit,
      averageHotspotRxRewardUnits: apiResponse.average_rx_reward_unit,
      averageHotspotPocRewardScore: apiResponse.average_poc_reward_score,
      histograms: apiResponse.histograms,
      histogram_percentiles: apiResponse.histogram_percentiles,
      prices: {
        [CurrencyKey.eur]: apiResponse.coingecko_price_eur,
        [CurrencyKey.gbp]: apiResponse.coingecko_price_gbp,
        [CurrencyKey.usd]: apiResponse.coingecko_price_usd,
        [CurrencyKey.cad]: apiResponse.coingecko_price_cad,
        [CurrencyKey.aud]: apiResponse.coingecko_price_aud,
        [CurrencyKey.brl]: apiResponse.coingecko_price_brl,
        [CurrencyKey.jpy]: apiResponse.coingecko_price_jpy,
        [CurrencyKey.dkk]: apiResponse.coingecko_price_dkk,
        [CurrencyKey.sek]: apiResponse.coingecko_price_sek,
        [CurrencyKey.chf]: apiResponse.coingecko_price_chf,
        [CurrencyKey.inr]: apiResponse.coingecko_price_inr,
        [CurrencyKey.cny]: apiResponse.coingecko_price_cny,
        [CurrencyKey.myr]: apiResponse.coingecko_price_myr,
        [CurrencyKey.sgd]: apiResponse.coingecko_price_sgd,
        [CurrencyKey.nzd]: apiResponse.coingecko_price_nzd,
        [CurrencyKey.nzd]: apiResponse.coingecko_price_nzd,
        [CurrencyKey.btc]: apiResponse.coingecko_price_btc,
        [CurrencyKey.eth]: apiResponse.coingecko_price_eth
      }
    };
  }, []);

  const { data: stats, error: isError } = useSWR("stats", fetchStats, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshWhenHidden: false
  });

  const isLoading = useMemo(() => !isError && !stats, [isError, stats]);

  const getPercentile = useCallback(
    (metric: string, value: number) => {
      const percentileValues: number[] = get(
        stats,
        ["histogram_percentiles", metric],
        []
      );

      if (isEmpty(percentileValues)) return undefined;

      let percentile = 0;

      percentileValues.forEach((percentileValue, index) => {
        if (value > percentileValue) {
          percentile = index + 1;
        }
      });

      return percentile;
    },
    [stats]
  );

  const value = useMemo(
    () => ({ stats, isLoading, isError, getPercentile }),
    [stats, isLoading, isError, getPercentile]
  );

  return <StatsContext.Provider value={value} {...props} />;
};

export const useStats = (): ContextValue => {
  const context = useContext(StatsContext);
  if (context === undefined) {
    throw new Error("useStats must be used within an StatsProvider");
  }
  return context;
};

//
// Utils
//

export interface HistogramData {
  boundary: number;
  count: number;
}

interface HistogramValuesMap {
  inactive: HistogramData;
  terrible: HistogramData;
  poor: HistogramData;
  mediocre: HistogramData;
  good: HistogramData;
  excellent: HistogramData;
}

interface StatsResponse {
  total_blocks: number;
  total_hotspots: number;
  total_consensus_groups: number;
  total_cities: number;
  total_countries: number;
  average_reward_scale: number;
  daily_average_hotspot_rewards: number;
  average_tx_reward_unit: number;
  average_rx_reward_unit: number;
  average_poc_reward_score: number;
  histograms: {
    poc_score: HistogramValuesMap;
    witnesses: HistogramValuesMap;
    witness_score: HistogramValuesMap;
    transmit_scale: HistogramValuesMap;
  };
  histogram_percentiles: {
    poc_score: number[];
    witnesses: number[];
    witness_score: number[];
    transmit_scale: number[];
  };
  coingecko_price_eur: number;
  coingecko_price_gbp: number;
  coingecko_price_usd: number;
  coingecko_price_cad: number;
  coingecko_price_aud: number;
  coingecko_price_brl: number;
  coingecko_price_jpy: number;
  coingecko_price_dkk: number;
  coingecko_price_sek: number;
  coingecko_price_btc: number;
  coingecko_price_chf: number;
  coingecko_price_inr: number;
  coingecko_price_myr: number;
  coingecko_price_sgd: number;
  coingecko_price_nzd: number;
  coingecko_price_cny: number;
  coingecko_price_eth: number;
}

interface Stats {
  blocks: number;
  hotspots: number;
  consensusGroups: number;
  cities: number;
  countries: number;
  averageTransmitScale: number;
  dailyAverageHotspotRewards: number;
  averageHotspotTxRewardUnits: number;
  averageHotspotRxRewardUnits: number;
  averageHotspotPocRewardScore: number;
  histograms: {
    poc_score: HistogramValuesMap;
    witnesses: HistogramValuesMap;
    witness_score: HistogramValuesMap;
    transmit_scale: HistogramValuesMap;
  };
  histogram_percentiles: {
    poc_score: number[];
    witnesses: number[];
    witness_score: number[];
    transmit_scale: number[];
  };
  prices: PricesMap;
}

interface PricesMap {
  [currency: string]: number;
}

export const renderPercentileClassification = (
  percentile: number | undefined
) => {
  if (percentile == null) return;

  if (percentile === 0) return "inactive";

  if (percentile < 50) return `bottom ${percentile}%`;
  return `top ${Math.max(100 - percentile, 1)}%`;
};
